import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  FormHelperText,
  Chip,
  CircularProgress,
  InputAdornment,
  Alert,
  Grid,
  Autocomplete,
  Tooltip,
  FormGroup,
  Stack,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  fetchCoupons,
  createCoupon,
  updateCoupon,
  deleteCoupon,
} from '../../store/slices/couponSlice';
import { fetchCouponUsages } from '../../store/slices/couponUsageSlice';
import AdminLayout from '../../components/layout/AdminLayout';
import ConfirmDialog from '../../components/ConfirmDialog';
import { formatDate } from '../../utils/formatters';

/**
 * Admin page for managing coupons
 * @returns {JSX.Element} The admin coupons page component
 */
const AdminCouponsPage = () => {
  const { t } = useTranslation('coupon');
  const dispatch = useDispatch();

  // Redux state
  const { coupons, status, error, totalPages, currentPage } = useSelector((state) => state.coupons);
  const { usages, status: usageStatus } = useSelector((state) => state.couponUsages);

  // Local state
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    type: 'percentage',
    value: '',
    minPurchase: '',
    maxUses: '',
    productRestrictions: null,
    categoryRestrictions: null,
    isActive: true,
    startDate: null,
    endDate: null,
    maxUsesPerUser: '',
    customerGroupRestrictions: null,
    minQuantity: '',
    combinable: true,
  });

  // Load coupons on mount and when filters change
  useEffect(() => {
    const params = {
      page: page + 1,
      limit: rowsPerPage,
      search: searchTerm,
    };

    if (filterActive !== 'all') {
      params.isActive = filterActive === 'active';
    }

    dispatch(fetchCoupons(params));
    // Also fetch coupon usages to get usage counts
    dispatch(fetchCouponUsages({ limit: 1000 })); // Fetch with a high limit to get all usages
  }, [dispatch, page, rowsPerPage, searchTerm, filterActive]);

  // Calculate coupon usage counts
  const couponUsageCounts = useMemo(() => {
    const counts = {};

    if (usages && usages.length > 0) {
      usages.forEach((usage) => {
        if (usage.couponId) {
          counts[usage.couponId] = (counts[usage.couponId] || 0) + 1;
        }
      });
    }

    return counts;
  }, [usages]);

  // Derived data
  const loading = status === 'loading';

  // Handle dialog actions
  const handleOpenDialog = (mode, coupon = null) => {
    setDialogMode(mode);
    setFormErrors({});

    if (mode === 'edit' && coupon) {
      setSelectedCoupon(coupon);
      setFormData({
        code: coupon.code || '',
        description: coupon.description || '',
        type: coupon.type || 'percentage',
        value: coupon.value || '',
        minPurchase: coupon.minPurchase || '',
        maxUses: coupon.maxUses || '',
        productRestrictions: coupon.productRestrictions || null,
        categoryRestrictions: coupon.categoryRestrictions || null,
        isActive: coupon.isActive ?? true,
        startDate: coupon.startDate ? new Date(coupon.startDate) : null,
        endDate: coupon.endDate ? new Date(coupon.endDate) : null,
        maxUsesPerUser: coupon.maxUsesPerUser || '',
        customerGroupRestrictions: coupon.customerGroupRestrictions || null,
        minQuantity: coupon.minQuantity || '',
        combinable: coupon.combinable ?? true,
      });

      // Refresh usage data when opening edit dialog
      dispatch(fetchCouponUsages({ couponCode: coupon.code }));
    } else {
      setSelectedCoupon(null);
      setFormData({
        code: '',
        description: '',
        type: 'percentage',
        value: '',
        minPurchase: '',
        maxUses: '',
        productRestrictions: null,
        categoryRestrictions: null,
        isActive: true,
        startDate: null,
        endDate: null,
        maxUsesPerUser: '',
        customerGroupRestrictions: null,
        minQuantity: '',
        combinable: true,
      });
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDeleteDialog = (coupon) => {
    setSelectedCoupon(coupon);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  // Form handling
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle different input types
    const inputValue = type === 'checkbox' ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: inputValue,
    }));

    // Clear error for this field when modified
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleDateChange = (name, date) => {
    setFormData((prev) => ({
      ...prev,
      [name]: date,
    }));

    // Clear error for this field when modified
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  // Validation
  const validateForm = () => {
    const errors = {};

    if (!formData.code.trim()) {
      errors.code = t('form.validationErrors.codeRequired');
    }

    if (!formData.type) {
      errors.type = t('form.validationErrors.typeRequired');
    }

    if (formData.type !== 'free_shipping' && !formData.value) {
      errors.value = t('form.validationErrors.valueRequired');
    }

    if (formData.type === 'percentage' && (formData.value < 0 || formData.value > 100)) {
      errors.value = t('form.validationErrors.percentageRange');
    }

    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      errors.endDate = t('form.validationErrors.endDateAfterStart');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save coupon
  const handleSaveCoupon = async () => {
    if (!validateForm()) {
      return;
    }

    // Prepare data for API
    const couponData = {
      ...formData,
      code: formData.code.toUpperCase(),
      value: formData.type === 'free_shipping' ? 0 : parseFloat(formData.value),
      minPurchase: formData.minPurchase ? parseFloat(formData.minPurchase) : null,
      maxUses: formData.maxUses ? parseInt(formData.maxUses, 10) : null,
      maxUsesPerUser: formData.maxUsesPerUser ? parseInt(formData.maxUsesPerUser, 10) : null,
      minQuantity: formData.minQuantity ? parseInt(formData.minQuantity, 10) : null,
    };

    try {
      if (dialogMode === 'create') {
        await dispatch(createCoupon(couponData)).unwrap();
      } else {
        await dispatch(
          updateCoupon({
            id: selectedCoupon.id,
            data: couponData,
          }),
        ).unwrap();
      }

      handleCloseDialog();
      // Refresh the list
      dispatch(
        fetchCoupons({
          page: page + 1,
          limit: rowsPerPage,
          search: searchTerm,
          isActive: filterActive !== 'all' ? filterActive === 'active' : undefined,
        }),
      );
    } catch (err) {
      console.error('Failed to save coupon:', err);
    }
  };

  // Delete coupon
  const handleDeleteCoupon = async () => {
    if (!selectedCoupon) return;

    try {
      await dispatch(deleteCoupon(selectedCoupon.id)).unwrap();
      handleCloseDeleteDialog();

      // Refresh the list
      dispatch(
        fetchCoupons({
          page: page + 1,
          limit: rowsPerPage,
          search: searchTerm,
          isActive: filterActive !== 'all' ? filterActive === 'active' : undefined,
        }),
      );
    } catch (err) {
      console.error('Failed to delete coupon:', err);
    }
  };

  // Pagination handling
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter handling
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const handleFilterChange = (e) => {
    setFilterActive(e.target.value);
    setPage(0);
  };

  // Generate the coupon types menu items
  const couponTypeOptions = useMemo(
    () => [
      { value: 'percentage', label: t('types.percentage') },
      { value: 'fixed_amount', label: t('types.fixed_amount') },
      { value: 'free_shipping', label: t('types.free_shipping') },
    ],
    [t],
  );

  // Get the usage count for a coupon
  const getUsageCount = (couponId) => {
    return couponUsageCounts[couponId] || 0;
  };

  return (
    <AdminLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
          >
            {t('management.title')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('create')}
          >
            {t('management.addButton')}
          </Button>
        </Box>

        {/* Filters */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <TextField
            label={t('filters.search')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ flexGrow: 1, maxWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl
            variant="outlined"
            size="small"
            sx={{ minWidth: 150 }}
          >
            <InputLabel>{t('filters.status')}</InputLabel>
            <Select
              value={filterActive}
              onChange={handleFilterChange}
              label={t('filters.status')}
            >
              <MenuItem value="all">{t('filters.all')}</MenuItem>
              <MenuItem value="active">{t('filters.active')}</MenuItem>
              <MenuItem value="inactive">{t('filters.inactive')}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Error message */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
          >
            {error}
          </Alert>
        )}

        {/* Coupons Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 640 }}>
            <Table
              stickyHeader
              aria-label="coupons table"
            >
              <TableHead>
                <TableRow>
                  <TableCell>{t('table.code')}</TableCell>
                  <TableCell>{t('table.type')}</TableCell>
                  <TableCell>{t('table.value')}</TableCell>
                  <TableCell>{t('table.minPurchase')}</TableCell>
                  <TableCell>{t('table.usage')}</TableCell>
                  <TableCell>{t('table.validPeriod')}</TableCell>
                  <TableCell>{t('table.status')}</TableCell>
                  <TableCell>{t('table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      align="center"
                      sx={{ py: 3 }}
                    >
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : coupons.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      align="center"
                      sx={{ py: 3 }}
                    >
                      {t('management.noResults')}
                    </TableCell>
                  </TableRow>
                ) : (
                  coupons.map((coupon) => (
                    <TableRow key={coupon.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography fontWeight="bold">{coupon.code}</Typography>
                          {!coupon.combinable && (
                            <Tooltip title={t('form.notCombinableHelp')}>
                              <Chip
                                size="small"
                                label={t('table.exclusive')}
                                color="info"
                              />
                            </Tooltip>
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                        >
                          {coupon.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {coupon.type === 'percentage'
                          ? t('types.percentage')
                          : coupon.type === 'fixed_amount'
                          ? t('types.fixed_amount')
                          : t('types.free_shipping')}
                      </TableCell>
                      <TableCell>
                        {coupon.type === 'percentage'
                          ? `${coupon.value}%`
                          : coupon.type === 'fixed_amount'
                          ? `${coupon.value} CHF`
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {coupon.minPurchase ? `${coupon.minPurchase} CHF` : '-'}
                      </TableCell>
                      <TableCell>
                        {getUsageCount(coupon.id)} / {coupon.maxUses || '∞'}
                      </TableCell>
                      <TableCell>
                        {coupon.startDate && coupon.endDate
                          ? `${formatDate(coupon.startDate)} - ${formatDate(coupon.endDate)}`
                          : coupon.startDate
                          ? `${t('dates.from')} ${formatDate(coupon.startDate)}`
                          : coupon.endDate
                          ? `${t('dates.until')} ${formatDate(coupon.endDate)}`
                          : t('table.noLimit')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={coupon.isActive ? t('status.active') : t('status.inactive')}
                          color={coupon.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleOpenDialog('edit', coupon)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleOpenDeleteDialog(coupon)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalPages * rowsPerPage}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      {/* Add/Edit Coupon Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === 'create' ? t('management.createTitle') : t('management.editTitle')}
        </DialogTitle>
        <DialogContent dividers>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid
              container
              spacing={2}
            >
              {/* Basic Information */}
              <Grid
                item
                xs={12}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  gutterBottom
                >
                  {t('form.basicInfo')}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <TextField
                  name="code"
                  label={t('form.code')}
                  fullWidth
                  required
                  value={formData.code}
                  onChange={handleInputChange}
                  error={!!formErrors.code}
                  helperText={formErrors.code}
                  disabled={dialogMode === 'edit'}
                  InputProps={{
                    style: { textTransform: 'uppercase' },
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <FormControl
                  fullWidth
                  required
                  error={!!formErrors.type}
                >
                  <InputLabel>{t('form.type')}</InputLabel>
                  <Select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    label={t('form.type')}
                  >
                    {couponTypeOptions.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.type && <FormHelperText>{formErrors.type}</FormHelperText>}
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
              >
                <TextField
                  name="description"
                  label={t('form.description')}
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.description || ''}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Value & Limits */}
              <Grid
                item
                xs={12}
              >
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ mt: 2 }}
                >
                  {t('form.valueAndLimits')}
                </Typography>
              </Grid>

              {formData.type !== 'free_shipping' && (
                <Grid
                  item
                  xs={12}
                  sm={6}
                >
                  <TextField
                    name="value"
                    label={
                      formData.type === 'percentage'
                        ? t('form.discountPercentage')
                        : t('form.discountAmount')
                    }
                    fullWidth
                    required
                    type="number"
                    InputProps={{
                      endAdornment:
                        formData.type === 'percentage' ? (
                          <InputAdornment position="end">%</InputAdornment>
                        ) : (
                          <InputAdornment position="end">CHF</InputAdornment>
                        ),
                    }}
                    value={formData.value}
                    onChange={handleInputChange}
                    error={!!formErrors.value}
                    helperText={formErrors.value}
                  />
                </Grid>
              )}

              <Grid
                item
                xs={12}
                sm={6}
              >
                <TextField
                  name="minPurchase"
                  label={t('form.minPurchaseAmount')}
                  fullWidth
                  type="number"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">CHF</InputAdornment>,
                  }}
                  value={formData.minPurchase}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <TextField
                  name="minQuantity"
                  label={t('form.minQuantity')}
                  fullWidth
                  type="number"
                  value={formData.minQuantity}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <TextField
                  name="maxUses"
                  label={t('form.maxTotalUses')}
                  fullWidth
                  type="number"
                  value={formData.maxUses}
                  onChange={handleInputChange}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <TextField
                  name="maxUsesPerUser"
                  label={t('form.maxUsesPerUser')}
                  fullWidth
                  type="number"
                  value={formData.maxUsesPerUser}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Date Range */}
              <Grid
                item
                xs={12}
              >
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ mt: 2 }}
                >
                  {t('form.validityPeriod')}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <DatePicker
                  label={t('form.startDate')}
                  value={formData.startDate}
                  onChange={(date) => handleDateChange('startDate', date)}
                  renderInput={(props) => (
                    <TextField
                      {...props}
                      fullWidth
                      variant="outlined"
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <DatePicker
                  label={t('form.endDate')}
                  value={formData.endDate}
                  onChange={(date) => handleDateChange('endDate', date)}
                  renderInput={(props) => (
                    <TextField
                      {...props}
                      fullWidth
                      variant="outlined"
                      error={!!formErrors.endDate || props.error}
                      helperText={formErrors.endDate || props.helperText}
                    />
                  )}
                />
              </Grid>

              {/* Settings */}
              <Grid
                item
                xs={12}
              >
                <Divider sx={{ my: 1 }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ mt: 2 }}
                >
                  {t('form.settings')}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                    }
                    label={t('form.active')}
                  />
                  <FormHelperText>
                    {formData.isActive ? t('form.activeHelp') : t('form.inactiveHelp')}
                  </FormHelperText>
                </FormGroup>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
              >
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        name="combinable"
                        checked={formData.combinable}
                        onChange={handleInputChange}
                      />
                    }
                    label={t('form.combinable')}
                  />
                  <FormHelperText>
                    {formData.combinable ? t('form.combinableHelp') : t('form.notCombinableHelp')}
                  </FormHelperText>
                </FormGroup>
              </Grid>

              {/* Usage Info for Edit Mode */}
              {dialogMode === 'edit' && selectedCoupon && (
                <Grid
                  item
                  xs={12}
                >
                  <Divider sx={{ my: 1 }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                    sx={{ mt: 2 }}
                  >
                    {t('form.usageInfo')}
                  </Typography>
                  <Typography variant="body2">
                    {t('form.timesUsed')}: {getUsageCount(selectedCoupon.id)}
                    {selectedCoupon.maxUses ? ` / ${selectedCoupon.maxUses}` : ''}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('actions.cancel')}</Button>
          <Button
            onClick={handleSaveCoupon}
            variant="contained"
            color="primary"
          >
            {t('actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={openDeleteDialog}
        title={t('management.deleteTitle')}
        message={t('management.deleteConfirmation')}
        onConfirm={handleDeleteCoupon}
        onCancel={handleCloseDeleteDialog}
        onClose={handleCloseDeleteDialog}
      />
    </AdminLayout>
  );
};

export default AdminCouponsPage;
